import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { PenelopDao, PenelopHistorySearch } from '@app/dao/penelop/penelop.dao';
import { Big } from 'big.js';
import * as _ from 'lodash';
import moment from 'moment';
import { takeWhile } from 'rxjs';

interface HistoryRow {
  contratId: string;
  date: string;
  sommeInvestissement: number;
  sommeRachat: number;
  valeurActuelle: number;
  valeurRachat: number;
  allocations: Allocation[];
  expanded?: boolean;
}

interface Allocation {
  id: string;
  isin: string;
  name: string;
  date: string;
  montant: number;
  parts: number;
  partPrice: number;
  indicateurPmv: number;
  plusOuMoinsValue: number;
  plusOuMoinsValueFormatted?: string;
  plusOuMoinsValueClass?: string;
}

@Component({
    selector: 'asset-history-table',
    templateUrl: './asset-history-table.component.html',
    styleUrls: ['./asset-history-table.component.scss'],
    standalone: false
})
export class AssetHistoryTableComponent implements OnInit, OnDestroy {
  @Input() contratId: string;

  alive = true;
  loading = false;
  historyData: HistoryRow[] = [];

  filterMinDate: string;
  filterMaxDate: string;

  currentPage = 0;
  pageSize = 10;
  totalElements = 0;
  totalPages = 0;

  constructor(private penelopDao: PenelopDao) {}

  ngOnInit() {
    if (this.contratId) {
      this.filterMinDate = moment().subtract(1, 'year').format('YYYY-MM-DD');
      this.loadHistories();
    }
  }

  ngOnDestroy() {
    this.alive = false;
  }

  loadHistories() {
    this.loading = true;

    const payload: PenelopHistorySearch = {
      page: this.currentPage,
      size: this.pageSize,
      sorts: [
        {
          property: 'DATE',
          direction: 'DESC',
        },
      ],
    };

    if (this.filterMinDate || this.filterMaxDate) {
      payload.date = {
        min: this.filterMinDate || null,
        max: this.filterMaxDate || null,
      };
    }

    this.penelopDao
      .searchHistories(this.contratId, payload)
      .pipe(takeWhile(() => this.alive))
      .subscribe(
        response => {
          this.historyData = response.content.map(item => ({
            ...item,
            expanded: false,
            allocations:
              item.allocations?.map(alloc => this.formatAllocation(alloc)) ||
              [],
          }));
          this.totalElements = response.totalElements;
          this.totalPages = response.totalPages;
          this.loading = false;
        },
        error => {
          console.error('Error loading histories:', error);
          this.loading = false;
        },
      );
  }

  formatAllocation(allocation: Allocation): Allocation {
    const value = allocation.plusOuMoinsValue;

    if (_.isNil(value)) {
      return {
        ...allocation,
        plusOuMoinsValueFormatted: 'N/C',
        plusOuMoinsValueClass: 'pmv-neutral',
      };
    }

    const percentage = new Big(value).mul(100).round(2).toString() + ' %';

    if (value >= 0) {
      return {
        ...allocation,
        plusOuMoinsValueFormatted: percentage,
        plusOuMoinsValueClass: 'pmv-positive',
      };
    } else {
      return {
        ...allocation,
        plusOuMoinsValueFormatted: percentage,
        plusOuMoinsValueClass: 'pmv-negative',
      };
    }
  }

  toggleRow(row: HistoryRow) {
    row.expanded = !row.expanded;
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.loadHistories();
  }

  onPrevious() {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.loadHistories();
    }
  }

  onNext() {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
      this.loadHistories();
    }
  }

  get hasPrevious(): boolean {
    return this.currentPage > 0;
  }

  get hasNext(): boolean {
    return this.currentPage < this.totalPages - 1;
  }

  get pageNumbers(): number[] {
    const pages = [];
    const maxPages = 10;
    let startPage = Math.max(0, this.currentPage - Math.floor(maxPages / 2));
    let endPage = Math.min(this.totalPages - 1, startPage + maxPages - 1);

    if (endPage - startPage < maxPages - 1) {
      startPage = Math.max(0, endPage - maxPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  applyDateFilter() {
    this.currentPage = 0;
    this.loadHistories();
  }

  resetDateFilter() {
    this.filterMinDate = moment().subtract(1, 'year').format('YYYY-MM-DD');
    this.filterMaxDate = null;
    this.currentPage = 0;
    this.loadHistories();
  }
}
